import React, { useState } from 'react';
import { ComponentWithReplacement } from '../../types';
import { Building } from '../../../../../types/protocols';

interface BulkApplySettingsModalProps {
    sourceComponent: ComponentWithReplacement;
    targetComponents: ComponentWithReplacement[];
    building: Building;
    onApply: (targetKeys: string[]) => void;
    onClose: () => void;
}

/**
 * Modal for bulk applying maintenance settings from one component to others.
 * Shows a checkbox list of target components (all selected by default).
 */
export const BulkApplySettingsModal: React.FC<BulkApplySettingsModalProps> = ({
    sourceComponent,
    targetComponents,
    building,
    onApply,
    onClose
}) => {
    // Start with all selected (common case is "apply to all")
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
        () => new Set(targetComponents.map(c => c.component.key))
    );

    const toggleComponent = (key: string) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleApply = () => {
        onApply(Array.from(selectedKeys));
    };

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '450px' }}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Kopiera inställningar</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Stäng"
                            />
                        </div>
                        <div className="modal-body">
                            <p className="mb-3">
                                Kopiera inställningar från{' '}
                                <strong>{sourceComponent.component.displayName(building)}</strong>{' '}
                                till:
                            </p>

                            <div className="bulk-apply-list">
                                {targetComponents.map(comp => {
                                    const key = comp.component.key;
                                    return (
                                        <label key={key} className="bulk-apply-item">
                                            <input
                                                type="checkbox"
                                                checked={selectedKeys.has(key)}
                                                onChange={() => toggleComponent(key)}
                                            />
                                            <span className="bulk-apply-item-name">
                                                {comp.component.displayName(building)}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Avbryt
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleApply}
                                disabled={selectedKeys.size === 0}
                            >
                                Tillämpa på {selectedKeys.size} komponenter
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
